// The Ask "propose" step -- `POST /api/ask/preview` in the design's own
// vocabulary (`#foundation` "Ask flow" gallery step 2, `#s07`).
//
// Per the 2026-09-06 UX execution plan §9 conflict #9: "Ask preview/commit...
// none has an M1 endpoint... hooks return `unavailable`, pages render the
// honest state. Fixtures drive tests and the dev harness only." There is
// nothing to fetch, so this hook does NOT attempt a network call in normal
// operation -- it resolves synchronously to an error every caller can map to
// the same "Ask lands in M2" honest state (WS-5 step 7), rather than
// guaranteeing a 404 against a real backend on every keystroke.
//
// The zod schemas below exist so the type this hook WOULD return is pinned
// now (the LLM proposes labels, never ids -- the server resolves them, per
// the binding rule), and so `ask.fixtures.ts` has a live contract to be
// checked against for the dev harness and colocated tests.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import { confidenceLevelSchema } from './useInboxItems';
import { askDraftResponseSchema, askPreviewResponseSchema } from './askSchemas';
import type { AskDraft } from './askSchemas';
export {
  askDraftResponseSchema,
  askPreviewResponseSchema,
  askDraftSchema,
} from './askSchemas';
export type { AskDraft, AskReviewRow, AskAnalyticalResult } from './askSchemas';

export const askMatchRowSchema = z.object({
  id: z.string(),
  merchant: z.string(),
  account_last4: z.string().nullable(),
  posted_at: z.string(),
  pending: z.boolean(),
  amount: z.string(),
  currency: z.string(),
  confidence: confidenceLevelSchema,
  struck: z.boolean(), // bank-amended/removed since match, per the design's "Adobe" row
});
export type AskMatchRow = z.infer<typeof askMatchRowSchema>;

export const askProposalSchema = z.object({
  draft_id: z.string(),
  sentence: z.string(),
  window: z.object({ start: z.string(), end: z.string(), clustered: z.boolean() }),
  account_last4s: z.array(z.string()),
  event_name: z.string(),
  categories: z.array(z.string()),
  location: z.string().nullable(),
  matches: z.array(askMatchRowSchema),
  likely_related: z.array(askMatchRowSchema),
  degraded_sources: z.array(z.object({ institution_name: z.string(), reason: z.string() })),
  expires_at: z.string(),
  // Present when a sync landed matches since a PRIOR preview of the same
  // draft (i.e. this IS a re-preview response) -- drives the
  // `preview-superseded` banner (`#s07` tablet). Absent/0 on a first preview.
  new_matches_since_last_preview: z.number().nullable().optional(),
});
export type AskProposal = z.infer<typeof askProposalSchema>;

export type AskPreviewInput = {
  sentence: string;
  scope?: { account_id: string[]; date_from?: string; date_to?: string };
  prior_draft_id?: string | null;
  operationId?: string;
};

const askIdSchema = z.string().uuid();

function operationId(input: AskPreviewInput): string {
  const requested = input.operationId ?? globalThis.crypto?.randomUUID?.();
  if (!requested) throw new Error('operation ID generation is unavailable');
  return askIdSchema.parse(requested);
}

function previewBody(input: AskPreviewInput) {
  const scope = input.scope ?? { account_id: [] };
  return {
    operation_id: operationId(input),
    sentence: input.sentence,
    scope,
    ...(input.prior_draft_id === undefined ? {} : { prior_draft_id: input.prior_draft_id }),
  };
}

export function useAskPreview() {
  const operationIdRef = useRef<string>();
  const controllerRef = useRef<AbortController | null>(null);
  const mutation = useMutation<AskDraft, Error, AskPreviewInput>({
    mutationFn: async (input) => {
      const body = previewBody(input);
      operationIdRef.current = body.operation_id;
      const controller = new AbortController();
      controllerRef.current = controller;
      const response = await client.post('/api/ask/preview', body, { signal: controller.signal });
      return parseEnvelope(askPreviewResponseSchema, response.data).data;
    },
    retry: false,
  });

  return {
    ...mutation,
    cancel: () => controllerRef.current?.abort(),
    get operationId() {
      return operationIdRef.current;
    },
  };
}

export function useAskDraft(draftId: string | null | undefined, cursor: string | null = null, limit = 200) {
  return useQuery({
    queryKey: ['ask', draftId, cursor, limit],
    enabled: Boolean(draftId),
    queryFn: async () => {
      const response = await client.get(`/api/ask/${askIdSchema.parse(draftId)}`, {
        params: { ...(cursor ? { cursor } : {}), limit },
      });
      return parseEnvelope(askDraftResponseSchema, response.data).data;
    },
    retry: false,
  });
}

export function useDiscardAskDraft() {
  const queryClient = useQueryClient();
  return useMutation<{ discarded: true }, Error, string>({
    mutationFn: async (draftId) => {
      const response = await client.delete(`/api/ask/${askIdSchema.parse(draftId)}`);
      return parseEnvelope(z.object({ discarded: z.literal(true) }).strict(), response.data).data;
    },
    retry: false,
    onSuccess: (_result, draftId) => {
      queryClient.invalidateQueries({ queryKey: ['ask', draftId] });
    },
  });
}
